/* Hallmark · macrostructure: Ecosystem Index · pre-emit critique: P5 H5 E4 S5 R5 V5 · contrast: pass (40–41) · nav: existing shell · footer: none · slop: pass */
/**
 * Dashboard Page
 *
 * Participant's home view with stats cards, continue learning, and upcoming evaluations.
 */

import { useMemo } from 'react';
import { ProgressBar } from '@lms/ui';
// Removed MOCK_UPCOMING_EVALUATIONS
import {
  CheckCircle,
  Loader2,
  ArrowRight,
  Sparkles,
  Star,
  FileText,
  MessageSquare,
  ShieldCheck,
  Megaphone,
  ChevronRight,
  AlertCircle,
  Calendar,
  User,
  Globe,
  ExternalLink,
  Award,
  BookOpen,
} from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '../store/auth';
import { useEnrollments } from '../hooks/useEnrollments';
import { useRegistrations } from '../hooks/useRegistrations';
import { useActiveAnnouncements } from '../hooks/useAnnouncements';
import { AnnouncementDetailModal } from '../components/AnnouncementDetailModal';
import { Link } from 'react-router';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import type { Announcement } from '@lms/shared/types';
import { resolveMediaUrl } from '@lms/shared/utils';
import { apiBaseUrl } from '../lib/api-client';

export function Dashboard() {
  const { user } = useAuthStore();
  const [activityReferenceTime] = useState(() => Date.now());
  const { data: enrollments, isLoading: envLoading } = useEnrollments({ userId: user?.id });
  const { data: registrationsResponse, isLoading: regLoading } = useRegistrations(user?.id);
  const { data: activeAnnouncements } = useActiveAnnouncements();
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  // `getByUser` returns { items, meta } — unwrap once for the rest of the page.
  const registrations = registrationsResponse?.items ?? [];
  const { t, language } = useLanguage();

  // Local date formatter for the registration card timestamps. Uses
  // the same `ar-EG` / `id-ID` locales as the Programs.tsx `formatDate`
  // helper so the same date renders identically on both pages.
  const formatRegDate = (date: Date | string | undefined) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString(language === 'ar' ? 'ar-EG' : 'id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const isLoading = envLoading || regLoading;

  // Average progress across all enrolled programs
  // NOTE: these must be computed before any early return (Rules of Hooks)
  const totalCompleted =
    enrollments && enrollments.length > 0
      ? enrollments.reduce((acc, curr) => acc + (curr.progress || 0), 0) / enrollments.length
      : 0;

  // Dynamic study consistency and schedule personalization
  const allCompletions = useMemo(() => {
    return enrollments?.flatMap(e => e.completions || []) || [];
  }, [enrollments]);

  const { activeDaysOfWeek, recentCompletionsCount, insightTitle, insightDesc } = useMemo(() => {
    const days = new Set<number>();
    const now = new Date(activityReferenceTime);
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    // 1. Get active days of the week (last 7 days)
    allCompletions.forEach(c => {
      if (c.completedAt) {
        const date = new Date(c.completedAt);
        if (date >= sevenDaysAgo && date <= now) {
          days.add(date.getDay());
        }
      }
    });

    // Use only recorded completion data. Do not fabricate growth percentages.
    const completionsLastWeek = allCompletions.filter(c => {
      if (!c.completedAt) return false;
      const date = new Date(c.completedAt);
      return date >= sevenDaysAgo && date <= now;
    }).length;

    const title =
      completionsLastWeek > 0
        ? {
            id: copy.dashboard.recentActivityTitle.id.replace(
              '{count}',
              String(completionsLastWeek)
            ),
            ar: copy.dashboard.recentActivityTitle.ar.replace(
              '{count}',
              String(completionsLastWeek)
            ),
          }
        : allCompletions.length > 0
          ? copy.dashboard.resumeRhythmTitle
          : copy.dashboard.aiInsightsStartTitle;

    // 3. Find the most common hour of completion to recommend the next study time
    const activeHours = allCompletions.map(c => {
      if (!c.completedAt) return 9;
      return new Date(c.completedAt).getHours();
    });

    let bestTimeOfDay = 'pagi';
    let bestTimeHour = 9;
    let bestTimeOfDayAr = 'الصباحية';

    if (activeHours.length > 0) {
      const hourCounts = new Array(24).fill(0);
      activeHours.forEach(h => hourCounts[h]++);
      const maxHour = hourCounts.indexOf(Math.max(...hourCounts));
      bestTimeHour = maxHour;
      if (maxHour >= 5 && maxHour < 11) {
        bestTimeOfDay = 'pagi';
        bestTimeOfDayAr = 'الصباحية';
      } else if (maxHour >= 11 && maxHour < 15) {
        bestTimeOfDay = 'siang';
        bestTimeOfDayAr = 'الظهيرة';
      } else if (maxHour >= 15 && maxHour < 18) {
        bestTimeOfDay = 'sore';
        bestTimeOfDayAr = 'المسائية';
      } else {
        bestTimeOfDay = 'malam';
        bestTimeOfDayAr = 'المسائية';
      }
    }

    const formatHourId = `${bestTimeHour % 12 || 12} ${bestTimeHour >= 12 ? 'siang/malam' : 'pagi'}`;
    const formatHourAr = `${bestTimeHour % 12 || 12} ${bestTimeHour >= 12 ? 'مساءً' : 'صباحاً'}`;

    const desc =
      allCompletions.length > 0
        ? {
            id: copy.dashboard.observedStudyTime.id
              .replace('{timeOfDay}', bestTimeOfDay)
              .replace('{hour}', formatHourId),
            ar: copy.dashboard.observedStudyTime.ar
              .replace('{timeOfDayAr}', bestTimeOfDayAr)
              .replace('{hourAr}', formatHourAr),
          }
        : copy.dashboard.aiInsightsDescFallback;

    return {
      activeDaysOfWeek: days,
      recentCompletionsCount: completionsLastWeek,
      insightTitle: title,
      insightDesc: desc,
    };
  }, [activityReferenceTime, allCompletions]);

  // Aggregate all upcoming live sessions / Tatap Muka (Google Meet / Zoom) across active programs
  const upcomingMeetings = useMemo(() => {
    if (!enrollments) return [];
    const list: Array<{
      id: string;
      title: string;
      programName: string;
      subjectName: string;
      meetingUrl: string;
      sessionNumber: number;
      duration?: number;
      isCompleted: boolean;
      materialId?: string;
    }> = [];

    enrollments
      .filter(e => e.status === 'ACTIVE')
      .forEach(enrollment => {
        const completedMatIds = new Set(enrollment.completions?.map(c => c.materialId) || []);

        enrollment.program.levels?.forEach(lvl => {
          lvl.stages?.forEach(stg => {
            stg.subjects?.forEach(sub => {
              sub.sessions?.forEach(ses => {
                const sessionMaterials = ses.materials || [];
                const meetingUrl =
                  ses.meetingUrl ||
                  sessionMaterials.find(material => material.meetingUrl)?.meetingUrl;
                if (meetingUrl) {
                  list.push({
                    id: ses.id,
                    title: ses.title,
                    programName: enrollment.program.name,
                    subjectName: sub.name,
                    meetingUrl,
                    sessionNumber: ses.sessionNumber,
                    duration: ses.duration,
                    isCompleted:
                      sessionMaterials.length > 0 &&
                      sessionMaterials.every(material => completedMatIds.has(material.id)),
                    materialId: sessionMaterials.find(
                      material => !completedMatIds.has(material.id)
                    )?.id,
                  });
                }
              });
            });
          });
        });
      });

    return list;
  }, [enrollments]);

  // Days since last active lesson completion
  const daysSinceLastActivity = useMemo(() => {
    if (!allCompletions || allCompletions.length === 0) return 0;
    const latestDate = allCompletions.reduce((latest, curr) => {
      if (!curr.completedAt) return latest;
      const d = new Date(curr.completedAt).getTime();
      return d > latest ? d : latest;
    }, 0);
    if (!latestDate) return 0;
    const diffMs = activityReferenceTime - latestDate;
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }, [activityReferenceTime, allCompletions]);

  // Find all pending or remediation stage/level evaluations that are ready to be taken
  const activeEvaluations = useMemo(() => {
    if (!enrollments) return [];
    const list: Array<{
      id: string;
      title: string;
      programName: string;
      programId: string;
      contextLabel: string;
      passingScore: number;
      isRemedial: boolean;
      score?: number;
    }> = [];

    enrollments
      .filter(e => e.status === 'ACTIVE')
      .forEach(enrollment => {
        const completedMatIds = new Set(enrollment.completions?.map(c => c.materialId) || []);

        enrollment.program.levels?.forEach(lvl => {
          lvl.stages?.forEach(stg => {
            const stgMaterials =
              stg.subjects?.flatMap(
                sub => sub.sessions?.flatMap(ses => ses.materials || []) || []
              ) || [];
            const allStgDone =
              stgMaterials.length > 0 && stgMaterials.every(m => completedMatIds.has(m.id));

            if (allStgDone && stg.evaluations) {
              stg.evaluations.forEach(ev => {
                const res = enrollment.results?.find(r => {
                  const meta = r.metadata as Record<string, unknown> | undefined;
                  const rUnknown = r as unknown as Record<string, unknown>;
                  const resultEvalId =
                    (meta?.evaluationId as string | undefined) ||
                    (rUnknown.evaluationId as string | undefined);
                  return resultEvalId === ev.id;
                });

                const meta = res?.metadata as Record<string, unknown> | undefined;
                const rUnknown = res as unknown as Record<string, unknown>;
                const isPassed =
                  meta?.isPassed !== undefined
                    ? Boolean(meta.isPassed)
                    : Boolean(rUnknown?.isPassed);
                const score =
                  res?.score ??
                  (meta?.score as number | undefined) ??
                  (rUnknown?.score as number | undefined);

                if (!res) {
                  list.push({
                    id: ev.id,
                    title: ev.title,
                    programName: enrollment.program.name,
                    programId: enrollment.programId,
                    contextLabel: `Tahap ${stg.stageCode || ''} • ${stg.name}`,
                    passingScore: ev.passingScore ?? stg.passingScore ?? 70,
                    isRemedial: false,
                  });
                } else if (!isPassed) {
                  list.push({
                    id: ev.id,
                    title: ev.title,
                    programName: enrollment.program.name,
                    programId: enrollment.programId,
                    contextLabel: `Tahap ${stg.stageCode || ''} • ${stg.name}`,
                    passingScore: ev.passingScore ?? stg.passingScore ?? 70,
                    isRemedial: true,
                    score: score !== undefined ? Math.round(Number(score)) : 0,
                  });
                }
              });
            }
          });
        });
      });

    return list;
  }, [enrollments]);

  // Early return after all hooks — must not be before any hook calls (Rules of Hooks)
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <p className="text-sm font-body text-on-surface-variant">
          {t(copy.dashboard.gatheringProgress)}
        </p>
      </div>
    );
  }

  // Check for Remediation / Perlu Penguatan states
  // const programsNeedingStrengthening = enrollments?.filter(e =>
  //   e.results?.some(r =>
  //     r.type === 'COMPLETION' &&
  //     (r.description?.toUpperCase().includes('PERLU PENGUATAN') ||
  //      (r.metadata as Record<string, unknown>)?.status === 'PERLU_PENGUATAN')
  //   )
  // ) || [];

  const upcomingTasks =
    enrollments
      ?.filter(e => e.status === 'ACTIVE')
      .flatMap(enrollment => {
        const materials = [
          ...(enrollment.program.levels?.flatMap(
            l =>
              l.stages?.flatMap(
                s =>
                  s.subjects?.flatMap(
                    sub => sub.sessions?.flatMap(ses => ses.materials || []) || []
                  ) || []
              ) || []
          ) || []),
          ...(enrollment.program.levels?.flatMap(
            l => l.flows?.flatMap(f => f.materials || []) || []
          ) || []),
          ...(enrollment.program.flows?.flatMap(f => f.materials || []) || []),
        ];
        const requiredMaterials = materials.filter(m => m.isRequired);
        const completedMaterialIds = enrollment.completions?.map(c => c.materialId) || [];

        const pendingMaterials = requiredMaterials.filter(
          m => !completedMaterialIds.includes(m.id)
        );
        const nextMaterial = pendingMaterials.length > 0 ? pendingMaterials[0] : null;

        const tasks = [];

        if (nextMaterial) {
          tasks.push({
            id: `mat-${nextMaterial.id}`,
            title: nextMaterial.title,
            programTitle: enrollment.program.name,
            link: `/materials/${nextMaterial.id}`,
            status: 'learning',
            dueDate: t(copy.dashboard.continueLearning),
          });
        } else {
          tasks.push({
            id: `eval-${enrollment.programId}`,
            title: t(copy.programDetail.assessmentsCompletion),
            programTitle: enrollment.program.name,
            link: `/programs/${enrollment.programId}`,
            status: 'pending',
            dueDate: t(copy.common.pending),
          });
        }
        return tasks;
      }) || [];

  const quickLinks = [
    { to: '/programs', label: copy.shell.myPrograms, icon: BookOpen },
    { to: '/materials', label: copy.materials.title, icon: FileText },
    { to: '/evaluations', label: copy.evaluations.title, icon: Award },
    { to: '/results', label: copy.shell.results, icon: Star },
  ];

  const programEnrollments = enrollments ?? [];
  const activeProgramCount = programEnrollments.filter(item => item.status === 'ACTIVE').length;
  const featuredEnrollment =
    programEnrollments.find(item => item.status === 'ACTIVE') ?? programEnrollments[0];
  const otherEnrollments = featuredEnrollment
    ? programEnrollments.filter(item => item.id !== featuredEnrollment.id)
    : [];
  const insightAction = upcomingTasks[0]
    ? { to: upcomingTasks[0].link, label: copy.dashboard.openNextStep }
    : featuredEnrollment
      ? { to: `/programs/${featuredEnrollment.programId}`, label: copy.dashboard.openProgram }
      : { to: '/programs', label: copy.dashboard.browsePrograms };

  return (
    <div className="space-y-10 animate-fadeIn pb-12">
      {/* Primary learning focus: first screen answers where to go next. */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6 rounded-[2.5rem] bg-surface-container-low p-5 sm:p-8 lg:p-10">
        <div className="lg:col-span-7 flex flex-col justify-between gap-8 min-w-0">
          <div className="space-y-4">
            <span className="inline-flex w-fit items-center gap-2 rounded-full bg-primary/10 px-3 py-1.5 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">
              <BookOpen size={14} aria-hidden="true" />
              <Bilingual {...copy.dashboard.learningHub} />
            </span>
            <h1 className="m-0 max-w-2xl text-3xl font-extrabold leading-tight tracking-tight text-on-surface sm:text-4xl lg:text-5xl">
              <Bilingual {...copy.dashboard.welcome} />, {user?.name?.split(' ')[0]}!{' '}
              <span className="block text-primary-container">
                <Bilingual {...copy.dashboard.readyToContinue} />
              </span>
            </h1>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <Link
              to={upcomingTasks[0]?.link || '/programs'}
              className="group inline-flex w-full items-center justify-center gap-3 rounded-lg bg-primary px-6 py-3.5 text-sm font-bold text-on-primary no-underline transition-colors hover:bg-primary-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95 sm:w-auto"
            >
              <span>{t(copy.dashboard.continueLearning)}</span>
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <div className="min-w-0 rounded-2xl bg-surface-container-lowest px-4 py-3 text-start shadow-level1 sm:max-w-xs">
              <p className="mb-1 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-on-surface-variant">
                <Bilingual {...copy.dashboard.nextMilestone} />
              </p>
              <p className="m-0 truncate text-sm font-bold text-on-surface">
                {upcomingTasks[0]?.title || t(copy.dashboard.browsePrograms)}
              </p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 min-w-0">
          <aside className="flex h-full min-h-64 flex-col justify-between gap-6 overflow-hidden rounded-[2rem] bg-primary p-6 text-on-primary shadow-level2 sm:p-8">
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-4">
                <Sparkles size={24} className="text-secondary" aria-hidden="true" />
                <span className="rounded-full bg-on-primary/10 px-3 py-1 text-[10px] font-label font-bold uppercase tracking-[0.14em]">
                  <Bilingual {...copy.dashboard.aiInsights} />
                </span>
              </div>
              <h2 className="m-0 text-2xl font-extrabold leading-tight">
                <Bilingual {...insightTitle} />
              </h2>
              <p className="m-0 max-w-[60ch] text-sm leading-relaxed text-on-primary/80">
                <Bilingual {...insightDesc} />
              </p>
            </div>

            <div className="space-y-4">
              <div
                className="grid grid-cols-3 gap-2"
                aria-label={t(copy.dashboard.learningSummary)}
              >
                <Link
                  to="/programs"
                  className="rounded-xl px-2 py-2 text-on-primary no-underline transition-colors hover:bg-on-primary/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-primary active:scale-[0.98]"
                >
                  <span className="block text-[9px] font-label font-bold uppercase tracking-wider text-on-primary/65">
                    <Bilingual {...copy.dashboard.progressShort} />
                  </span>
                  <span className="mt-1 block text-xl font-extrabold">
                    {Math.round(totalCompleted)}%
                  </span>
                  <span className="mt-1 block text-[10px] font-bold text-secondary">
                    <Bilingual {...copy.dashboard.viewProgress} />
                  </span>
                </Link>
                <Link
                  to="/programs"
                  className="rounded-xl px-2 py-2 text-on-primary no-underline transition-colors hover:bg-on-primary/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-primary active:scale-[0.98]"
                >
                  <span className="block text-[9px] font-label font-bold uppercase tracking-wider text-on-primary/65">
                    <Bilingual {...copy.dashboard.activeProgramsShort} />
                  </span>
                  <span className="mt-1 block text-xl font-extrabold">{activeProgramCount}</span>
                  <span className="mt-1 block text-[10px] font-bold text-secondary">
                    <Bilingual {...copy.dashboard.viewPrograms} />
                  </span>
                </Link>
                <Link
                  to="/materials"
                  className="rounded-xl px-2 py-2 text-on-primary no-underline transition-colors hover:bg-on-primary/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-primary active:scale-[0.98]"
                >
                  <span className="block text-[9px] font-label font-bold uppercase tracking-wider text-on-primary/65">
                    <Bilingual {...copy.dashboard.lastSevenDaysShort} />
                  </span>
                  <span className="mt-1 block text-xl font-extrabold">
                    {recentCompletionsCount}
                  </span>
                  <span className="mt-1 block text-[10px] font-bold text-secondary">
                    <Bilingual {...copy.dashboard.viewActivity} />
                  </span>
                </Link>
              </div>

              <div
                className="flex items-center gap-2"
                aria-label={t(copy.dashboard.activeStudyDays)}
              >
                {copy.dashboard.dayAbbreviations.map((day, index) => (
                  <span
                    key={`${day.id}-${index}`}
                    className={`flex size-8 items-center justify-center rounded-full text-[10px] font-bold ${
                      activeDaysOfWeek.has(index)
                        ? 'bg-secondary text-on-secondary shadow-level1'
                        : 'bg-on-primary/10 text-on-primary/70'
                    }`}
                  >
                    <Bilingual {...day} />
                  </span>
                ))}
              </div>

              <Link
                to={insightAction.to}
                className="inline-flex w-fit items-center gap-2 rounded-lg bg-secondary px-4 py-2.5 text-xs font-bold text-on-secondary no-underline transition-colors hover:bg-secondary-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary active:scale-95"
              >
                <Bilingual {...insightAction.label} />
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <nav
        aria-label={t(copy.dashboard.quickAccess)}
        className="grid grid-cols-2 gap-3 sm:grid-cols-4"
      >
        {quickLinks.map(({ to, label, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            className="group flex min-h-24 flex-col justify-between rounded-2xl bg-surface-container-lowest p-4 text-on-surface no-underline shadow-level1 transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
          >
            <Icon size={20} className="text-primary" aria-hidden="true" />
            <span className="text-sm font-bold leading-tight group-hover:text-primary">
              <Bilingual {...label} />
            </span>
          </Link>
        ))}
      </nav>

      {/* Announcements Highlight Section (if active announcements exist) */}
      {activeAnnouncements && activeAnnouncements.length > 0 && (
        <section className="space-y-4 rounded-[2rem] bg-surface-container-lowest p-6 shadow-level1 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-primary/10 text-primary">
                <Megaphone size={20} />
              </div>
              <h2 className="text-lg sm:text-xl font-headline font-bold text-on-surface m-0">
                <Bilingual {...copy.announcements.latestAnnouncements} />
              </h2>
            </div>
            <Link
              to="/announcements"
              className="text-xs font-label font-bold text-primary hover:underline flex items-center gap-1"
            >
              <Bilingual {...copy.announcements.viewAll} />
              <ChevronRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
            {activeAnnouncements.slice(0, 2).map(ann => (
              <button
                key={ann.id}
                onClick={() => setSelectedAnnouncement(ann)}
                className="group flex min-h-44 flex-col justify-between rounded-2xl bg-surface-container-low p-5 text-start transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99]"
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-1.5">
                      {ann.priority === 'URGENT' && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-error-container text-on-error-container px-2 py-0.5 rounded-full">
                          <AlertCircle size={10} />
                          {t(copy.announcements.prioUrgent)}
                        </span>
                      )}
                      <span className="text-[10px] font-label font-bold bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {ann.category === 'ACADEMIC'
                          ? t(copy.announcements.catAcademic)
                          : ann.category === 'IMPORTANT'
                            ? t(copy.announcements.catImportant)
                            : ann.category === 'MAINTENANCE'
                              ? t(copy.announcements.catMaintenance)
                              : ann.category === 'EVENT'
                                ? t(copy.announcements.catEvent)
                                : t(copy.announcements.catGeneral)}
                      </span>
                    </div>
                    <span className="text-[10px] font-body text-on-surface-variant opacity-60 flex items-center gap-1">
                      <Calendar size={11} />
                      {new Date(ann.createdAt).toLocaleDateString(
                        language === 'ar' ? 'ar-EG' : 'id-ID',
                        { month: 'short', day: 'numeric' }
                      )}
                    </span>
                  </div>
                  <h3 className="font-headline font-bold text-sm text-on-surface group-hover:text-primary transition-colors line-clamp-1 mb-1">
                    {ann.title}
                  </h3>
                  <p className="text-xs font-body text-on-surface-variant line-clamp-2 leading-relaxed opacity-75">
                    {ann.content}
                  </p>
                </div>
                <div className="mt-4 flex items-center gap-1 pt-1 text-[11px] font-label font-bold text-primary group-hover:underline">
                  <Bilingual {...copy.announcements.readMore} />
                  <ChevronRight size={12} />
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Live Tatap Muka (Google Meet / Zoom) Section */}
      {upcomingMeetings.length > 0 && (
        <section className="space-y-4 rounded-[2rem] bg-gradient-to-r from-emerald-500/10 via-primary/5 to-surface-container-lowest p-6 shadow-level1 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary text-on-primary shadow-level1">
                <Globe size={22} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-primary">
                    <span className="size-1.5 rounded-full bg-primary animate-pulse" />
                    <Bilingual {...copy.dashboard.liveSessionLabel} />
                  </span>
                </div>
                <h2 className="text-lg sm:text-xl font-headline font-bold text-on-surface m-0 mt-0.5">
                  <Bilingual {...copy.dashboard.liveSessionsTitle} />
                </h2>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
            {upcomingMeetings.slice(0, 4).map(meet => (
              <div
                key={meet.id}
                className="flex flex-col justify-between gap-3 rounded-2xl bg-surface-container-lowest p-5 shadow-level1 transition-colors hover:bg-surface-container-high"
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-md truncate max-w-[200px]">
                      {meet.programName}
                    </span>
                    <span className="text-[10px] text-on-surface-variant font-mono font-bold">
                      #{meet.sessionNumber}
                    </span>
                  </div>
                  <h3 className="font-headline font-bold text-sm text-on-surface line-clamp-1">
                    {meet.title}
                  </h3>
                  <p className="text-xs text-on-surface-variant line-clamp-1 mt-0.5">
                    {meet.subjectName}{' '}
                    {meet.duration ? `• ${meet.duration} ${t(copy.common.minutes)}` : ''}
                  </p>
                </div>

                <div className="flex items-center justify-between gap-2 pt-2">
                  {meet.materialId && (
                    <Link
                      to={`/materials/${meet.materialId}`}
                      className="text-xs font-bold text-on-surface-variant hover:text-primary transition-colors no-underline flex items-center gap-1"
                    >
                      <Bilingual {...copy.dashboard.openMaterial} />
                      <ArrowRight size={13} />
                    </Link>
                  )}
                  <a
                    href={meet.meetingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-xs font-bold text-on-primary no-underline transition-colors hover:bg-primary-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
                  >
                    <Bilingual {...copy.dashboard.joinLiveSession} />
                    <ExternalLink size={13} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Active & Remedial Evaluations Section */}
      {activeEvaluations.length > 0 && (
        <section className="space-y-4 rounded-[2rem] bg-gradient-to-r from-secondary/15 via-primary/5 to-surface-container-lowest p-6 shadow-level1 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-secondary text-on-secondary shadow-level1">
                <Award size={22} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-secondary/15 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-secondary">
                    <Bilingual {...copy.dashboard.pendingCountBadge} /> ({activeEvaluations.length})
                  </span>
                </div>
                <h2 className="text-lg sm:text-xl font-headline font-bold text-on-surface m-0 mt-0.5">
                  <Bilingual {...copy.dashboard.pendingEvaluationsAlertTitle} />
                </h2>
                <p className="text-xs text-on-surface-variant m-0 mt-0.5 font-body">
                  <Bilingual {...copy.dashboard.pendingEvaluationsAlertDesc} />
                </p>
              </div>
            </div>

            <Link
              to="/evaluations"
              className="text-xs font-bold text-primary hover:underline flex items-center gap-1 no-underline"
            >
              <span>{t(copy.dashboard.viewAll)}</span>
              <ChevronRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
            {activeEvaluations.slice(0, 4).map(evalItem => (
              <div
                key={evalItem.id}
                className={`flex flex-col justify-between gap-3 rounded-2xl p-5 shadow-level1 transition-colors ${
                  evalItem.isRemedial
                    ? 'bg-warning-container/40 hover:bg-warning-container/60'
                    : 'bg-surface-container-lowest hover:bg-surface-container-high'
                }`}
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-md truncate max-w-[200px]">
                      {evalItem.programName}
                    </span>
                    <span className="text-[10px] text-on-surface-variant font-bold">
                      KKM: {evalItem.passingScore}%
                    </span>
                  </div>
                  <span className="text-[9px] font-bold text-secondary uppercase tracking-wider block">
                    {evalItem.contextLabel}
                  </span>
                  <h3 className="font-headline font-bold text-sm text-on-surface line-clamp-1 mt-0.5">
                    {evalItem.title}
                  </h3>
                  {evalItem.isRemedial && (
                    <p className="mt-1 text-xs font-medium text-on-warning-container">
                      Perlu Remedial (Nilai sebelumnya: {evalItem.score}%)
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between gap-2 pt-2">
                  <Link
                    to={`/programs/${evalItem.programId}`}
                    className="text-xs font-bold text-on-surface-variant hover:text-primary transition-colors no-underline flex items-center gap-1"
                  >
                    <span>Kurikulum</span>
                    <ChevronRight size={13} />
                  </Link>

                  <Link
                    to={`/evaluations/${evalItem.id}`}
                    className={`ml-auto inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-bold no-underline transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95 ${
                      evalItem.isRemedial
                        ? 'bg-tertiary text-on-tertiary hover:opacity-90'
                        : 'bg-secondary text-on-secondary hover:opacity-90'
                    }`}
                  >
                    <span>
                      {evalItem.isRemedial
                        ? t(copy.evaluations.retakeExamBtn)
                        : t(copy.evaluations.takeExamBtn)}
                    </span>
                    <ArrowRight size={13} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Inactivity Encouragement Banner if inactive >= 7 days and no pending evaluations */}
      {daysSinceLastActivity >= 7 && activeEvaluations.length === 0 && (
        <section className="space-y-4 rounded-[2rem] bg-gradient-to-r from-primary/10 via-primary/5 to-surface-container-lowest p-6 shadow-level1 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3.5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary text-on-primary shadow-level1">
                <Sparkles size={22} />
              </div>
              <div className="space-y-0.5">
                <h2 className="text-base sm:text-lg font-headline font-bold text-on-surface m-0">
                  <Bilingual {...copy.dashboard.inactivityNoticeTitle} />
                </h2>
                <p className="text-xs sm:text-sm text-on-surface-variant m-0 font-body leading-relaxed max-w-xl">
                  <Bilingual {...copy.dashboard.inactivityNoticeDesc} />
                </p>
              </div>
            </div>

            <Link
              to={upcomingTasks[0]?.link || '/programs'}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-xs font-bold text-on-primary no-underline transition-colors hover:bg-primary-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
            >
              <span>{t(copy.dashboard.actionResumeStudy)}</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      )}

      <section className="space-y-6 rounded-[2rem] bg-surface-container-lowest p-5 shadow-level1 sm:p-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="space-y-1">
            <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-primary">
              <Bilingual {...copy.dashboard.programCollection} />
            </p>
            <h2 className="m-0 text-2xl font-extrabold text-on-surface">
              <Bilingual {...copy.dashboard.myPrograms} />
            </h2>
          </div>
          <Link
            to="/programs"
            className="inline-flex items-center gap-2 rounded-lg bg-surface-container-low px-4 py-2.5 text-xs font-bold text-primary no-underline transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
          >
            <Bilingual {...copy.dashboard.viewAll} />
            <ArrowRight size={14} aria-hidden="true" />
          </Link>
        </div>

        {featuredEnrollment ? (
          <div className="grid gap-4 lg:grid-cols-12">
            <article
              className={`${otherEnrollments.length > 0 ? 'lg:col-span-7' : 'lg:col-span-12'} flex min-h-72 flex-col justify-between gap-8 rounded-[1.75rem] bg-primary p-6 text-on-primary shadow-level2 sm:p-8`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex size-12 items-center justify-center rounded-2xl bg-on-primary/10 text-secondary">
                  <BookOpen size={23} aria-hidden="true" />
                </div>
                <span className="rounded-full bg-on-primary/10 px-3 py-1 text-[10px] font-label font-bold uppercase tracking-wider">
                  {t(
                    copy.common.enrollmentStatus[
                      featuredEnrollment.status as keyof typeof copy.common.enrollmentStatus
                    ] || { id: featuredEnrollment.status, ar: featuredEnrollment.status }
                  )}
                </span>
              </div>

              <div className="space-y-5">
                <div className="space-y-2">
                  <p className="m-0 text-[10px] font-label font-bold uppercase tracking-[0.16em] text-on-primary/65">
                    <Bilingual {...copy.dashboard.featuredProgram} />
                  </p>
                  <h3 className="m-0 max-w-2xl text-2xl font-extrabold leading-tight sm:text-3xl">
                    {featuredEnrollment.program.name}
                  </h3>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs font-bold">
                    <span>
                      <Bilingual {...copy.dashboard.overallProgress} />
                    </span>
                    <span>{Math.round(featuredEnrollment.progress)}%</span>
                  </div>
                  <div
                    role="progressbar"
                    aria-label={t(copy.dashboard.overallProgress)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(featuredEnrollment.progress)}
                    className="h-2 overflow-hidden rounded-full bg-on-primary/15"
                  >
                    <span
                      className="block h-full rounded-full bg-secondary"
                      style={{
                        width: `${Math.min(100, Math.max(0, featuredEnrollment.progress))}%`,
                      }}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to={upcomingTasks[0]?.link || `/programs/${featuredEnrollment.programId}`}
                    className="inline-flex items-center gap-2 rounded-lg bg-secondary px-5 py-3 text-xs font-bold text-on-secondary no-underline transition-colors hover:bg-secondary-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary active:scale-95"
                  >
                    <Bilingual {...copy.dashboard.continueLearning} />
                    <ArrowRight size={14} aria-hidden="true" />
                  </Link>
                  <Link
                    to={`/programs/${featuredEnrollment.programId}`}
                    className="inline-flex items-center gap-2 rounded-lg bg-on-primary/10 px-5 py-3 text-xs font-bold text-on-primary no-underline transition-colors hover:bg-on-primary/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-on-primary active:scale-95"
                  >
                    <Bilingual {...copy.dashboard.openProgram} />
                  </Link>
                </div>
              </div>
            </article>

            {otherEnrollments.length > 0 && (
              <div className="space-y-3 lg:col-span-5">
                {otherEnrollments.slice(0, 4).map(item => (
                  <Link
                    key={item.id}
                    to={`/programs/${item.programId}`}
                    className="group flex min-h-24 items-center gap-4 rounded-2xl bg-surface-container-low p-4 text-on-surface no-underline transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99]"
                  >
                    {item.program.coverImage ? (
                      <img
                        src={resolveMediaUrl(item.program.coverImage, apiBaseUrl)}
                        alt=""
                        className="h-16 w-20 shrink-0 rounded-xl object-cover shadow-level1"
                      />
                    ) : (
                      <span className="flex h-16 w-20 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <BookOpen size={20} aria-hidden="true" />
                      </span>
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-bold group-hover:text-primary">
                        {item.program.name}
                      </span>
                      <span className="mt-2 block">
                        <ProgressBar value={item.progress} size="sm" />
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-2xl bg-surface-container-low p-10 text-center text-sm text-on-surface-variant">
            {t(copy.dashboard.noEnrollments)}
          </div>
        )}
      </section>

      {/* Main Dashboard Content */}
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {/* Upcoming Tasks (Span 2) */}
        <div className="flex flex-col rounded-[2rem] bg-surface-container-lowest p-8 shadow-level1 md:col-span-2">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-xl font-headline font-bold text-on-surface">
              <Bilingual {...copy.dashboard.actionRequired} />
            </h2>
            <Link
              to="/materials"
              className="text-primary text-xs font-bold uppercase tracking-widest hover:underline"
            >
              <Bilingual {...copy.dashboard.queue} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {upcomingTasks.slice(0, 4).map(task => (
              <Link
                key={task.id}
                to={task.link}
                className="group rounded-2xl bg-surface-container-low p-5 no-underline shadow-level1 transition-colors hover:bg-surface-container-high focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.99]"
              >
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-surface-container-lowest shadow-level1 transition-colors group-hover:bg-secondary group-hover:text-on-secondary">
                  <CheckCircle size={20} />
                </div>
                <h3 className="font-bold text-on-surface text-sm mb-1 line-clamp-1">
                  {task.title}
                </h3>
                <p className="text-[10px] text-on-surface-variant font-medium mb-3 line-clamp-1">
                  {task.programTitle}
                </p>
                <div className="flex items-center gap-1 text-[10px] font-bold text-primary uppercase tracking-tighter">
                  <span>{task.dueDate}</span>
                  <ArrowRight size={10} />
                </div>
              </Link>
            ))}
            {upcomingTasks.length === 0 && (
              <div className="col-span-full py-10 text-center italic text-on-surface-variant text-sm">
                {t(copy.dashboard.allUpToDate)}
              </div>
            )}
          </div>
        </div>

        {/* Registration Tracking & Onboarding Checklist */}
        <div className="flex flex-col rounded-[2rem] bg-surface-container-lowest p-8 shadow-level1">
          <h2 className="text-xl font-headline font-bold text-on-surface mb-8">
            <Bilingual {...copy.dashboard.registrationOnboarding} />
          </h2>
          <div className="space-y-6">
            {registrations && registrations.length > 0 ? (
              registrations.map(reg => (
                <div key={reg.id} className="space-y-4">
                  <div className="space-y-3 rounded-2xl bg-surface-container-low p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold text-sm text-on-surface leading-tight">
                          {reg.program?.name}
                        </p>
                        <p className="text-[9px] font-label font-bold text-on-surface-variant uppercase tracking-widest mt-1">
                          {t(
                            copy.common.registrationStatus[
                              reg.status as keyof typeof copy.common.registrationStatus
                            ] || { id: reg.status, ar: reg.status }
                          )}
                        </p>
                      </div>
                      {reg.status === 'APPROVED' && (
                        <Link
                          to={`/receipt/${reg.id}`}
                          className="p-2 bg-primary/10 text-primary rounded-lg hover:bg-primary hover:text-on-primary transition-all"
                          title={t(copy.dashboard.downloadReceipt)}
                        >
                          <FileText size={16} />
                        </Link>
                      )}
                    </div>
                    {/*
                      Submission / resubmission timestamps. The
                      `lastResubmittedAt` field is sticky: it is set when
                      the user re-submits after a previous REJECTED /
                      REVISION / CANCELLED, and stays set even if the row
                      goes back to REJECTED later. Render the second row
                      whenever it is present so the user can see at a
                      glance whether the row has been touched since the
                      original submission.
                    */}
                    <div className="space-y-1 rounded-xl bg-surface-container-lowest/70 p-2.5">
                      <p className="text-[9px] font-mono text-on-surface-variant/80 flex items-center justify-between">
                        <span>
                          <Bilingual {...copy.programs.submittedOn} />
                        </span>
                        <span className="font-semibold">{formatRegDate(reg.registeredAt)}</span>
                      </p>
                      {reg.lastResubmittedAt && (
                        <p className="text-[9px] font-mono text-on-surface-variant/80 flex items-center justify-between">
                          <span>
                            <Bilingual {...copy.programs.resubmittedOn} />
                          </span>
                          <span className="font-semibold">
                            {formatRegDate(reg.lastResubmittedAt)}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Onboarding Checklist (Only for APPROVED) */}
                  {reg.status === 'APPROVED' && (
                    <div className="space-y-4 rounded-2xl bg-primary/5 p-5">
                      <p className="text-[10px] font-label font-bold text-primary uppercase tracking-widest">
                        <Bilingual {...copy.dashboard.onboardingChecklist} />
                      </p>
                      <div className="space-y-3">
                        {[
                          { key: 'WA_JOINED', label: copy.dashboard.waStep, icon: MessageSquare },
                          {
                            key: 'PROFILE_COMPLETED',
                            label: copy.dashboard.profileStep,
                            icon: User,
                          },
                          {
                            key: 'CONSENT_SIGNED',
                            label: copy.dashboard.consentStep,
                            icon: ShieldCheck,
                          },
                          {
                            key: 'ORIENTATION_WATCHED',
                            label: copy.dashboard.orientationStep,
                            icon: FileText,
                          },
                        ].map(step => {
                          const statusOrder = [
                            'INITIAL',
                            'WA_JOINED',
                            'PROFILE_COMPLETED',
                            'CONSENT_SIGNED',
                            'ORIENTATION_WATCHED',
                            'COMPLETED',
                          ];
                          const currentIndex = statusOrder.indexOf(
                            reg.onboardingStatus || 'INITIAL'
                          );
                          const stepIndex = statusOrder.indexOf(step.key);
                          const isDone = stepIndex <= currentIndex;

                          return (
                            <div key={step.key} className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`p-1.5 rounded-lg ${isDone ? 'bg-success/10 text-success' : 'bg-on-surface/5 text-on-surface-variant'}`}
                                >
                                  <step.icon size={14} />
                                </div>
                                <span
                                  className={`text-xs font-medium ${isDone ? 'text-on-surface line-through opacity-50' : 'text-on-surface'}`}
                                >
                                  <Bilingual {...step.label} />
                                </span>
                              </div>
                              {isDone ? (
                                <CheckCircle size={14} className="text-success" />
                              ) : (
                                <div className="w-4 h-4 rounded-full border-2 border-outline-variant" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {reg.participantId && (
                    <div className="rounded-2xl bg-surface-container-low p-4">
                      <p className="text-[8px] font-label font-bold text-on-surface-variant uppercase tracking-tighter opacity-60">
                        <Bilingual {...copy.dashboard.scholarIdentifier} />
                      </p>
                      <p className="text-xs font-mono font-bold text-primary">
                        {reg.participantId.participantCode}
                      </p>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-sm text-on-surface-variant italic py-4">
                {t(copy.dashboard.noPending)}
              </p>
            )}
          </div>
        </div>
      </div>

      <AnnouncementDetailModal
        announcement={selectedAnnouncement}
        isOpen={!!selectedAnnouncement}
        onClose={() => setSelectedAnnouncement(null)}
      />
    </div>
  );
}
